import type { Quiz, QuizTotal } from '@efdigital/ll-addon-data';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface Signature {
  Args: {
    total: QuizTotal;
    quiz: Quiz;
  };
}

type ScoringScope = 'whole-quiz' | 'specific-question';

export class EditQuizTotalCriteria extends Component<Signature> {
  @tracked scoringScope: ScoringScope = this.args.total.qid
    ? 'specific-question'
    : 'whole-quiz';

  get categories() {
    return this.args.quiz.categories ?? [];
  }

  get questions() {
    return this.args.quiz.questions ?? [];
  }

  setFrom = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.total.from = parseInt(target.value, 10);
  };

  setTo = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.total.to = parseInt(target.value, 10);
  };

  setCategory = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.args.total.category = target.value;
  };

  setScoringScope = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.scoringScope = target.value as ScoringScope;

    // Clear qid when switching to whole quiz
    if (this.scoringScope === 'whole-quiz') {
      this.args.total.qid = '';
    }
  };

  setQid = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.args.total.qid = target.value;
  };

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <div class="card-body">
        <p class="form-label">
          If the user scores...
        </p>
        <div class="row mb-3">
          <div class="col-md-6">
            <label for="total-from-{{uniqueId}}" class="form-label">
              Between...
            </label>
            <input
              type="number"
              class="form-control"
              id="total-from-{{uniqueId}}"
              value={{@total.from}}
              min="0"
              {{debounce this.setFrom}}
            />
          </div>
          <div class="col-md-6">
            <label for="total-to-{{uniqueId}}" class="form-label">
              and... (inclusive)
            </label>
            <input
              type="number"
              class="form-control"
              id="total-to-{{uniqueId}}"
              value={{@total.to}}
              min="0"
              {{debounce this.setTo}}
            />
          </div>
        </div>

        <div class="mb-3">
          <label for="total-category-{{uniqueId}}" class="form-label">
            In category...
          </label>
          <select
            class="form-select"
            id="total-category-{{uniqueId}}"
            {{on "change" this.setCategory}}
          >
            <option value="" selected={{not @total.category}}>
              Grand Total Score
            </option>
            {{#each this.categories as |category|}}
              <option
                value={{category.cid}}
                selected={{eq category.cid @total.category}}
              >
                {{category.title}}
              </option>
            {{/each}}
          </select>
        </div>

        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            name="scoring-scope-{{uniqueId}}"
            id="whole-quiz-{{uniqueId}}"
            value="whole-quiz"
            checked={{eq this.scoringScope "whole-quiz"}}
            {{on "change" this.setScoringScope}}
          />
          <label class="form-check-label" for="whole-quiz-{{uniqueId}}">
            Across the whole quiz
          </label>
        </div>
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            name="scoring-scope-{{uniqueId}}"
            id="specific-question-{{uniqueId}}"
            value="specific-question"
            checked={{eq this.scoringScope "specific-question"}}
            {{on "change" this.setScoringScope}}
          />
          <label class="form-check-label" for="specific-question-{{uniqueId}}">
            In a specific question
          </label>
        </div>

        {{#if (eq this.scoringScope "specific-question")}}
          <div class="mb-3">
            <label for="total-qid-{{uniqueId}}" class="form-label">
              Select question
            </label>
            <select
              class="form-select"
              id="total-qid-{{uniqueId}}"
              {{on "change" this.setQid}}
            >
              <option value="" selected={{not @total.qid}}>
                -- Select a question --
              </option>
              {{#each this.questions as |question|}}
                <option
                  value={{question.qid}}
                  selected={{eq question.qid @total.qid}}
                >
                  {{question.title}}
                </option>
              {{/each}}
            </select>
          </div>
        {{/if}}
      </div>
    {{/let}}
  </template>
}

function eq(a: string | undefined, b: string | undefined): boolean {
  return a === b;
}

function not(value: string | undefined): boolean {
  return !value;
}
